import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import PowerSelect from 'ember-power-select/components/power-select';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import { gt, or } from 'ember-truth-helpers';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type FlowModel from 'garaje/models/flow';
import type HostSignOutReminderConfigurationModel from 'garaje/models/host-sign-out-reminder-configuration';
import type LocationModel from 'garaje/models/location';
import onInsert from 'garaje/modifiers/on-insert';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { type DurationOption, DURATIONS } from 'garaje/utils/format-duration';

type DeliveryChannel = 'Push' | 'Email';

const DELIVERY_CHANNELS: DeliveryChannel[] = ['Push', 'Email'];

interface ReminderRow {
  configId: string | null;
  flow: FlowModel | null;
  duration: DurationOption | null;
}

interface HostSignOutReminderSignature {
  Args: {
    currentLocation: LocationModel;
  };
}

const DEFAULT_DURATION = DURATIONS[0]!;

function getAvailableFlows(rows: ReminderRow[], visitorFlows: FlowModel[], row: ReminderRow): FlowModel[] {
  const selectedFlowIds = rows.filter((r) => r !== row && r.flow).map((r) => r.flow!.id);
  return visitorFlows.filter((f) => !selectedFlowIds.includes(f.id));
}

function durationForMinutes(minutes: number): DurationOption {
  return DURATIONS.find((d) => d.value === minutes) ?? DEFAULT_DURATION;
}

export default class HostSignOutReminder extends Component<HostSignOutReminderSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare store: StoreService;

  @tracked isOpen = false;
  @tracked isSaving = false;
  @tracked isDisabling = false;
  @tracked rows: ReminderRow[] = [];
  @tracked flows: FlowModel[] = [];
  @tracked existingConfigs: HostSignOutReminderConfigurationModel[] = [];
  @tracked selectedDeliveryChannels: DeliveryChannel[] = [...DELIVERY_CHANNELS];

  durations = DURATIONS;
  deliveryChannels = DELIVERY_CHANNELS;

  get isEnabled(): boolean {
    return this.existingConfigs.length > 0;
  }

  get visitorFlows(): FlowModel[] {
    return this.flows.filter((f) => !f.employeeCentric);
  }

  async loadFlows(): Promise<void> {
    this.flows = (
      await this.store.query('flow', {
        filter: { location: this.args.currentLocation.id },
      })
    ).slice();
  }

  async loadExistingConfigs(): Promise<void> {
    const configs = await this.store.query('host-sign-out-reminder-configuration', {
      filter: { location_id: this.args.currentLocation.id },
    });
    this.existingConfigs = configs.slice();
  }

  rowFromConfig(config: HostSignOutReminderConfigurationModel): ReminderRow {
    const flow = this.flows.find((f) => f.id === (config.belongsTo('flow') as { id(): string }).id());
    return {
      configId: config.id,
      flow: flow ?? null,
      duration: durationForMinutes(config.reminderDurationMinutes),
    };
  }

  deliveryChannelsFromConfigs(): void {
    if (this.existingConfigs.length === 0) return;
    const first = this.existingConfigs[0]!;
    const channels: DeliveryChannel[] = [];
    if (first.pushEnabled) channels.push('Push');
    if (first.emailEnabled) channels.push('Email');
    this.selectedDeliveryChannels = channels.length > 0 ? channels : [...DELIVERY_CHANNELS];
  }

  @action
  async loadInitialState(): Promise<void> {
    try {
      await this.loadExistingConfigs();
    } catch {
      // silently fail on init – panel will show Enable button
    }
  }

  enableTask = dropTask(async () => {
    try {
      await this.loadFlows();
      await this.loadExistingConfigs();
      this.rows = [{ configId: null, flow: null, duration: DEFAULT_DURATION }];
      this.selectedDeliveryChannels = [...DELIVERY_CHANNELS];
      this.isOpen = true;
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  @action
  cancel(): void {
    this.isOpen = false;
    this.rows = [];
  }

  @action
  async edit(): Promise<void> {
    try {
      await this.loadFlows();
      await this.loadExistingConfigs();
      this.rows = this.existingConfigs.map((c) => this.rowFromConfig(c));
      this.deliveryChannelsFromConfigs();
      this.isOpen = true;
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  }

  @action
  async save(): Promise<void> {
    if (this.rows.some((r) => !r.flow)) {
      this.flashMessages.showAndHideFlash('error', 'Please select a visitor type for each row.');
      return;
    }
    if (this.selectedDeliveryChannels.length === 0) {
      this.flashMessages.showAndHideFlash('error', 'Please select at least one delivery method.');
      return;
    }
    this.isSaving = true;
    try {
      const pushEnabled = this.selectedDeliveryChannels.includes('Push');
      const emailEnabled = this.selectedDeliveryChannels.includes('Email');
      const existingIds = new Set(this.existingConfigs.map((c) => c.id));
      const currentIds = new Set(this.rows.filter((r) => r.configId).map((r) => r.configId));

      const toDelete = this.existingConfigs.filter((c) => !currentIds.has(c.id));
      for (const config of toDelete) {
        await config.destroyRecord();
      }

      for (const row of this.rows) {
        if (!row.flow || !row.duration) continue;

        if (row.configId && existingIds.has(row.configId)) {
          const config = this.existingConfigs.find((c) => c.id === row.configId)!;
          config.reminderDurationMinutes = row.duration.value;
          config.pushEnabled = pushEnabled;
          config.emailEnabled = emailEnabled;
          await config.save();
        } else {
          const config = this.store.createRecord('host-sign-out-reminder-configuration', {
            reminderDurationMinutes: row.duration.value,
            enabled: true,
            pushEnabled,
            emailEnabled,
            location: this.args.currentLocation,
            flow: row.flow,
          });
          await config.save();
        }
      }

      await this.loadExistingConfigs();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
      this.isOpen = false;
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    } finally {
      this.isSaving = false;
    }
  }

  @action
  async disable(): Promise<void> {
    this.isDisabling = true;
    try {
      for (const config of this.existingConfigs) {
        await config.destroyRecord();
      }
      this.existingConfigs = [];
      this.rows = [];
      this.isOpen = false;
      this.flashMessages.showAndHideFlash('success', 'Disabled!');
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    } finally {
      this.isDisabling = false;
    }
  }

  @action
  setFlow(row: ReminderRow, flow: FlowModel): void {
    this.rows = this.rows.map((r) => (r === row ? { ...r, flow } : r));
  }

  @action
  setDuration(row: ReminderRow, duration: DurationOption): void {
    this.rows = this.rows.map((r) => (r === row ? { ...r, duration } : r));
  }

  @action
  setDeliveryChannels(deliveryChannels: DeliveryChannel[]): void {
    this.selectedDeliveryChannels = deliveryChannels;
  }

  get canAddRow(): boolean {
    return this.rows.length < this.visitorFlows.length;
  }

  @action
  addRow(): void {
    this.rows = [...this.rows, { configId: null, flow: null, duration: DEFAULT_DURATION }];
  }

  @action
  removeRow(row: ReminderRow): void {
    this.rows = this.rows.filter((r) => r !== row);
  }

  <template>
    <SettingsPanel @isOpen={{this.isOpen}} data-test-host-sign-out-reminder {{onInsert this.loadInitialState}} as |sp|>
      {{#let sp.header sp.body sp.footer as |PanelHeader PanelBody PanelFooter|}}
        <PanelHeader
          @iconUri="/assets/images/features/host-signout-reminder.svg"
          @title="Host sign-out reminder"
          @onCancel={{this.cancel}}
          as |header|
        >
          <header.description>
            Remind hosts to sign out their visitors
          </header.description>
          <header.controls>
            {{#if sp.isOpen}}
              <button
                data-test-save-host-sign-out-reminder
                type="button"
                class="btn btn--small btn--brand"
                disabled={{or this.isSaving this.isDisabling}}
                {{on "click" this.save}}
              >
                {{if this.isSaving "Saving..." "Save"}}
              </button>
            {{else if this.isEnabled}}
              <button
                data-test-edit-host-sign-out-reminder
                type="button"
                class="btn btn--small btn--brand"
                {{on "click" this.edit}}
              >
                Edit
              </button>
            {{else}}
              <button
                data-test-enable-host-sign-out-reminder
                type="button"
                class="btn btn--small btn--brand"
                disabled={{this.enableTask.isRunning}}
                {{on "click" (perform this.enableTask)}}
              >
                {{if this.enableTask.isRunning "Loading..." "Enable"}}
              </button>
            {{/if}}
          </header.controls>
        </PanelHeader>

        <PanelBody as |PanelContent|>
          <PanelContent @style="inset">
            {{#each this.rows as |row|}}
              <div class="w-3/5 flex items-end gap-4 mb-2" data-test-reminder-row>
                <div class="flex-1">
                  <p class="text-sm font-medium mb-2">Visitor type</p>
                  <PowerSelect
                    data-test-visitor-type-dropdown
                    @placeholder="Select sign-in flows"
                    @ariaLabel="Visitor type"
                    @options={{getAvailableFlows this.rows this.visitorFlows row}}
                    @selected={{row.flow}}
                    @onChange={{fn this.setFlow row}}
                    as |flow|
                  >
                    {{flow.name}}
                  </PowerSelect>
                </div>
                <div class="flex-1">
                  <p class="text-sm font-medium mb-2">Duration after sign in</p>
                  <PowerSelect
                    data-test-duration-dropdown
                    @searchEnabled={{false}}
                    @placeholder="Select duration"
                    @ariaLabel="Duration after sign in"
                    @options={{this.durations}}
                    @selected={{row.duration}}
                    @onChange={{fn this.setDuration row}}
                    as |d|
                  >
                    {{d.label}}
                  </PowerSelect>
                </div>
                {{#if (gt this.rows.length 1)}}
                  <button
                    data-test-remove-row
                    type="button"
                    aria-label="Remove row"
                    class="text-gray-400 hover:text-red-500 pb-2"
                    {{on "click" (fn this.removeRow row)}}
                  >
                    <span class="icon-trash"></span>
                  </button>
                {{/if}}
              </div>
            {{/each}}
            {{#if this.canAddRow}}
              <button
                data-test-add-another
                type="button"
                class="btn btn--small btn--link !text-gem-60"
                {{on "click" this.addRow}}
              >
                + Add another
              </button>
            {{/if}}
            <div class="mt-6 w-3/5" data-test-delivery-methods>
              <p class="text-sm font-medium mb-2">Delivery methods</p>
              <PowerSelectMultiple
                data-test-delivery-method-dropdown
                @searchEnabled={{false}}
                @ariaLabel="Delivery methods"
                @options={{this.deliveryChannels}}
                @selected={{this.selectedDeliveryChannels}}
                @onChange={{this.setDeliveryChannels}}
                as |channel|
              >
                {{channel}}
              </PowerSelectMultiple>
            </div>
          </PanelContent>
          {{#if this.isEnabled}}
            <PanelFooter>
              <button
                data-test-disable-host-sign-out-reminder
                type="button"
                class="btn btn--small btn--outlineFill"
                disabled={{or this.isDisabling this.isSaving}}
                {{on "click" this.disable}}
              >
                {{if this.isDisabling "Disabling..." "Disable"}}
              </button>
            </PanelFooter>
          {{/if}}
        </PanelBody>
      {{/let}}
    </SettingsPanel>
  </template>
}
